import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setHapticsEnabled, setLanguage } from '@/store/settings.slice';
import { useLogtoAuth } from '@/hooks/useLogtoAuth';
import { AppearanceSettings } from '@/components/settings/AppearanceSettings';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'it', label: 'Italiano' },
] as const;

export function Settings() {
  const { t, i18n } = useTranslation('general');
  const dispatch = useAppDispatch();
  const settings = useAppSelector((s) => s.settings);
  const { signOut } = useLogtoAuth();

  const handleLanguage = (code: string) => {
    dispatch(setLanguage(code));
    void i18n.changeLanguage(code);
  };

  return (
    <div className="flex flex-col gap-6 px-4 pb-6 pt-5">
      <header>
        <h1 className="text-2xl font-bold text-fg">
          {t('settings', { defaultValue: 'Settings' })}
        </h1>
      </header>

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          {t('language', { defaultValue: 'Language' })}
        </h2>
        <div className="flex flex-col gap-1 overflow-hidden rounded-xl bg-bg-secondary">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => handleLanguage(lang.code)}
              className="flex items-center justify-between px-3 py-3 text-left transition-opacity active:opacity-80"
            >
              <span className="text-sm text-fg">{lang.label}</span>
              {settings.language === lang.code && (
                <span className="text-xs font-medium text-brand">Active</span>
              )}
            </button>
          ))}
        </div>
      </section>

      <AppearanceSettings />

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          {t('preferences', { defaultValue: 'Preferences' })}
        </h2>
        <label className="flex items-center justify-between rounded-xl bg-bg-secondary p-3">
          <span className="text-sm text-fg">
            {t('haptics', { defaultValue: 'Haptic feedback' })}
          </span>
          <input
            type="checkbox"
            className="accent-brand"
            checked={settings.hapticsEnabled}
            onChange={(e) => dispatch(setHapticsEnabled(e.target.checked))}
          />
        </label>
      </section>

      <button
        type="button"
        onClick={signOut}
        className="rounded-xl bg-bg-secondary p-3 text-sm font-medium text-danger transition-opacity active:opacity-80"
      >
        {t('logout', { defaultValue: 'Sign out' })}
      </button>
    </div>
  );
}
